/**
 * WeekView — Uke i kalenderen (Plan 03-07; #157, steg 7a, D8).
 *
 * Uke er ukestripen i datopanelet + agendaen for den valgte dagen
 * (design/screens/Stell_Kalender.dc.html). Timerutenettet er borte (D8).
 *
 *   - `WeekDayStrip` ligger i datopanelet (CalendarRoute): sju dager med
 *     prikker per person som har noe den dagen. Et trykk velger dagen;
 *     den valgte dagen er ankeret, så forrige/neste flytter en uke og beholder
 *     ukedagen.
 *   - `WeekView` er agendaen under panelet: dagens overskrift, heldags-
 *     hendelsene først og så resten etter klokkeslett, som glassrader
 *     (EventCard `agenda`).
 *
 * Hvilken dag en hendelse hører til, avgjøres som før av `starts_at` i lokal
 * tid (D-14); gjentakelser er allerede utvidet av serveren (D-04).
 *
 * D-06: hand-rolled atop date-fns 4 + formatters.ts; no react-big-calendar.
 */
import { useMemo, type ReactNode } from 'react';
import { startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { WeekStrip } from '../../components/primitives/WeekStrip';
import { EmptyState } from '../../components/primitives';
import { capitalizeFirst, formatDayHeader } from '../../i18n/formatters';
import { sortTodayEvents } from '../dashboard/tiles/EventsTile';
import { EventCard } from './EventCard';
import { attendeesOf, dayDots, eventsOn, type AttendeeLookup } from './attendees';
import type { EventInstance } from './useCalendarRange';

interface WeekDayStripProps {
  /** Den valgte dagen; uka rundt den vises. */
  anchor: Date;
  today: Date;
  events: readonly EventInstance[];
  attendees: AttendeeLookup;
  onSelect: (day: Date) => void;
}

/** Ukestripen i datopanelet, med prikker per person. */
export function WeekDayStrip({ anchor, today, events, attendees, onSelect }: WeekDayStripProps): ReactNode {
  const { t } = useTranslation();
  // Ukas hendelser fordelt på dag én gang, ikke én gjennomgang per prikk og tekst.
  const byDay = useMemo(() => {
    const map = new Map<string, EventInstance[]>();
    for (const ev of events) {
      const key = startOfDay(new Date(ev.starts_at)).toISOString();
      map.set(key, [...(map.get(key) ?? []), ev]);
    }
    return map;
  }, [events]);
  const on = (day: Date) => byDay.get(startOfDay(day).toISOString()) ?? [];
  return (
    <div data-testid="week-day-strip" className="mt-3">
      <WeekStrip
        weekOf={anchor}
        today={today}
        selected={anchor}
        onSelect={onSelect}
        label={t('calendar.strip.label')}
        dots={(day) => dayDots(on(day), attendees)}
        dayHint={(day) => {
          const count = on(day).length;
          return count > 0 ? t('calendar.strip.events', { count }) : undefined;
        }}
      />
    </div>
  );
}

export interface WeekViewProps {
  /** Den valgte dagen, som agendaen viser. */
  anchor: Date;
  /** Hele ukas hendelser (spørringen gjelder uka). */
  events: EventInstance[];
  attendees: AttendeeLookup;
  onEventClick?: (e: EventInstance) => void;
  onEmptySlotClick?: (date: Date) => void;
}

/** Agendaen for den valgte dagen. */
export function WeekView({
  anchor,
  events,
  attendees,
  onEventClick,
  onEmptySlotClick,
}: WeekViewProps): ReactNode {
  const { t } = useTranslation();
  const dayEvents = sortTodayEvents(eventsOn(events, anchor), (e) => e.starts_at);
  const allDay = dayEvents.filter((e) => e.all_day);
  const timed = dayEvents.filter((e) => !e.all_day);
  const heading = capitalizeFirst(formatDayHeader(anchor));

  const row = (ev: EventInstance) => (
    <li key={ev.id}>
      <EventCard
        variant="agenda"
        id={ev.id}
        title={ev.title}
        starts_at={ev.starts_at}
        ends_at={ev.ends_at}
        all_day={ev.all_day}
        location={ev.location}
        color_token={ev.color_token}
        attendee_ids={ev.attendee_ids}
        attendees={attendeesOf(ev, attendees)}
        onClick={() => onEventClick?.(ev)}
      />
    </li>
  );

  return (
    <section
      aria-labelledby="week-agenda-heading"
      data-testid="week-view"
      className="flex flex-col gap-2.5 md:max-w-3xl"
    >
      <h2 id="week-agenda-heading" data-testid="week-agenda-heading" className="m-0 text-heading-s font-extrabold">
        {heading}
      </h2>

      {allDay.length > 0 && (
        <ul data-testid="week-allday-banner" className="m-0 flex list-none flex-col gap-2.5 p-0">
          {allDay.map(row)}
        </ul>
      )}

      {timed.length > 0 && (
        <ul data-testid="week-agenda" className="m-0 flex list-none flex-col gap-2.5 p-0">
          {timed.map(row)}
        </ul>
      )}

      {/* Plan 06-02 Task 2: <EmptyState variant="inline"> med
          calendar.empty.week.{heading,body} når hele uka er tom; ytre
          wrapper beholder week-empty-testid-en. En tom dag i en uke med
          hendelser får en kort linje og samme «Legg til hendelse» som Dag. */}
      {events.length === 0 ? (
        <div data-testid="week-empty">
          <EmptyState
            variant="inline"
            heading={t('calendar.empty.week.heading')}
            body={t('calendar.empty.week.body')}
            cta={{ label: t('calendar.action.addEvent'), onClick: () => onEmptySlotClick?.(anchor) }}
          />
        </div>
      ) : dayEvents.length === 0 ? (
        <div data-testid="week-day-empty">
          <EmptyState
            variant="inline"
            heading={t('calendar.agenda.emptyDay')}
            cta={{ label: t('calendar.action.addEvent'), onClick: () => onEmptySlotClick?.(anchor) }}
          />
        </div>
      ) : null}
    </section>
  );
}
